import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FileVersion } from '@global/constants';
import { WithDestroy } from '@lib/shared';
import { Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { LoanApiService } from '../../../services/loan-api.service';
import { UserRolesResolve, UserService } from '../../../user';
import { DialogService } from '../../services/dialog.service';
import { PipelineDataResolver } from '../../services/pipeline-data.resolver';
import { UsersApiService } from '../../services/users-api.service';

@Component({
  selector: 'app-loan-purpose',
  templateUrl: './loan-purpose.component.html',
  styleUrls: ['./loan-purpose.component.scss']
})
export class LoanPurposeComponent extends WithDestroy() implements OnInit, OnDestroy {
  private readonly role = 'Loan Officer,Sales';
  public loans: any[];
  private brokerUserNmls: number;
  public loanPurpose: string;
  public currentState: any;
  public options: any;
  public appStates: any = {
    ASK_LO: {
      eligible: false,
      completed: false
    },
    CHOOSE_LO: {
      eligible: false,
      completed: false
    }
  };
  public form: FormGroup;
  private defaultLeadAssigneeId: any;
  private defaultNMLS: any;
  public leadAssigneeId: any;
  public isBrokerAvailable = false;
  public isActiveLoan = false;
  public excludedStagesIds = [];
  private searchSubscription: Subscription;
  private posConfig: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loanApiService: LoanApiService,
    private userRolesResolve: UserRolesResolve,
    private pipelineDataResolverService: PipelineDataResolver,
    private userService: UserService,
    private location: Location,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private usersApiService: UsersApiService
  ) {
    super();
    this.brokerUserNmls = route.snapshot.params.nmls;
  }

  ngOnInit() {
    this.excludedStagesIds = this.pipelineDataResolverService.closedLoanStages;
    this.posConfig = this.route.snapshot.data.posOrgConfig.posConfig;
    this.form = this.formBuilder.group({
      isLoanOfficer: [null, [Validators.required]],
      loanOfficer: ['', [Validators.required]]
    });
    this.onFormChanges();
    this.userService
      .getActiveUser()
      .pipe(first())
      .subscribe((value: any) => {
        const nmlsLoanOfficerId = value?.LoanOfficerId || null;
        this.loans = value.Loans || this.route.snapshot.data.loans;
        this.isActiveLoan = this.loans.find((item) => !this.excludedStagesIds.includes(item.CurrentStage?.StageId));
        this.defaultLeadAssigneeId = value.WizeOrgUnit.Broker.LeadAssignee?.id || null;
        this.defaultNMLS = value.WizeOrgUnit.Broker.LeadAssignee?.nmlsId || null;
        if (!this.isActiveLoan) {
          this.isBrokerAvailable = this.isValidNmls(this.brokerUserNmls);
          if (!this.isBrokerAvailable || (this.isBrokerAvailable && !nmlsLoanOfficerId)) {
            if (this.defaultLeadAssigneeId) {
              this.currentState = 'ASK_LO';
              this.appStates.ASK_LO.eligible = true;
              this.appStates.CHOOSE_LO.eligible = true;
            } else {
              this.currentState = 'CHOOSE_LO';
              this.appStates.ASK_LO.eligible = false;
              this.appStates.CHOOSE_LO.eligible = true;
              this.getLoanOfficers('');
            }
          } else {
            this.currentState = 'DEFAULT';
            this.appStates.ASK_LO.eligible = false;
            this.appStates.CHOOSE_LO.eligible = false;
            this.leadAssigneeId = nmlsLoanOfficerId;
          }
        } else {
          this.router.navigate(['../' + 'select-loan'], { relativeTo: this.route });
        }
      });
  }

  private onFormChanges() {
    this.isLoanOfficer.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value) {
        this.resetLoanOfficer();
      } else {
        if (this.defaultLeadAssigneeId) {
          this.updateBroker(this.defaultLeadAssigneeId, this.defaultNMLS);
        }
        this.currentState = 'DEFAULT';
      }
    });
  }

  public loanOfficerSelected(selectedLoanOfficer: any) {
    const loanOfficer = this.options.find((lo) => lo.id === selectedLoanOfficer.id);
    if (loanOfficer) {
      this.updateBroker(loanOfficer.id, loanOfficer.nmlsId);
    } else {
      this.loanOfficer.setErrors({ invalid: true });
      this.leadAssigneeId = null;
    }
  }

  public createNewLoan() {
    if (this.loans?.length) {
      // show confirm dialog
      const confirmDialog = this.dialogService.open('confirm-dialog', {
        title: 'New Application',
        message: 'Are you sure to create new loan application?'
      });
      confirmDialog.onClose.subscribe((result) => {
        if (result) {
          this.createLoan();
        } else {
          this.loanPurpose = null;
        }
      });
    } else {
      this.createLoan();
    }
  }

  private createLoan() {
    this.currentState = 'LOAN_CREATION';
    const defPropUsageType = this.posConfig[this.loanPurpose.toLowerCase()].mortgage.defaultPropertyUsageType;
    const payload: any = {
      LoanPurposeType: this.loanPurpose,
      LoanOfficerId: this.leadAssigneeId || this.defaultLeadAssigneeId,
      fileVersion: FileVersion.URLA_2021,
      PropertyUsageType: defPropUsageType || ''
    };
    if (this.route.snapshot.params.relId) {
      payload.RealtorId = this.route.snapshot.params.relId;
    }
    this.loanApiService.createNewLoan(payload).subscribe(
      (value) => {
        // to update loan list in current user call
        this.userRolesResolve.resetCurrentUserWithACL(true);
        if (this.brokerUserNmls) {
          this.router.navigate([this.brokerUserNmls, 'pos', 'app', value.LoanId, 'application']);
        } else {
          this.router.navigate(['../' + 'select-loan'], { relativeTo: this.route });
        }
      },
      (error) => {
        this.currentState = 'DEFAULT';
      }
    );
  }

  private resetLoanOfficer() {
    this.loanOfficer.setValue('', { emitEvent: false });
    this.leadAssigneeId = null;
    this.currentState = 'CHOOSE_LO';
    this.getLoanOfficers('');
  }

  public back(currentState: string) {
    switch (currentState) {
      case 'CHOOSE_LO':
        if (this.appStates.ASK_LO.eligible) {
          this.currentState = 'ASK_LO';
          this.isLoanOfficer.setValue(null, { emitEvent: false });
        }
        break;
      case 'DEFAULT':
        if (
          this.appStates.CHOOSE_LO.eligible &&
          this.leadAssigneeId &&
          ((this.appStates.ASK_LO.eligible && this.isLoanOfficer.value && this.defaultLeadAssigneeId) || !this.appStates.ASK_LO.eligible)
        ) {
          this.resetLoanOfficer();
        } else if (this.appStates.ASK_LO.eligible && !this.isLoanOfficer.value) {
          this.currentState = 'ASK_LO';
          this.isLoanOfficer.setValue('', { emitEvent: false });
        } else {
          this.location.back();
        }
        break;
      default:
        this.location.back();
        break;
    }
  }

  public searchLoanOfficer(event) {
    this.leadAssigneeId = null;
    const searchedText = event.target.value.trim();
    if (!this.loanOfficer.invalid || searchedText.length === 0) {
      this.getLoanOfficers(searchedText);
    }
  }

  public getLoanOfficers(searchText) {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.searchSubscription = this.usersApiService.getUsers(searchText.trim(), this.role).subscribe(
      (los: any) =>
        (this.options = los?.map((lo) => {
          return {
            id: lo.id,
            name: lo.name,
            email: lo.email,
            nmlsId: lo.nmlsId,
            initials: `${lo.firstName.charAt(0)}${lo.lastName ? lo.firstName.charAt(0) : ''}`
          };
        }))
    );
  }

  public continue() {
    this.currentState = 'DEFAULT';
  }

  private isValidNmls(nmls) {
    return /^[a-zA-Z0-9-]+$/.test(nmls);
  }

  private updateBroker(id: any, nmls: any) {
    this.leadAssigneeId = id;
    this.brokerUserNmls = nmls;
    sessionStorage.setItem('nmls', nmls);
  }

  get isLoanOfficer() {
    return this.form.get('isLoanOfficer');
  }

  get loanOfficer() {
    return this.form.get('loanOfficer');
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
